import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client from "../../api/client";
import { useAuthStore } from "../../store/authStore";
import Spinner from "../../components/Spinner";

type LuckyInfo = {
  lucky_color: string;
  lucky_number: number;
  lucky_direction: string;
  lucky_food: string;
  advice: string;
  opportunity: string;
  caution: string;
  date: string;
  day_gan: string;
  day_ji: string;
  lotto_numbers: number[];
};

function LottoBall({ num }: { num: number }) {
  const color =
    num <= 10
      ? "bg-yellow-400 text-yellow-900"
      : num <= 20
        ? "bg-blue-500 text-white"
        : num <= 30
          ? "bg-red-500 text-white"
          : num <= 40
            ? "bg-gray-600 text-white"
            : "bg-green-500 text-white";
  return (
    <span
      className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-xs font-bold ${color}`}
    >
      {num}
    </span>
  );
}

function useCountdown() {
  const [remaining, setRemaining] = useState("");

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const midnight = new Date(now);
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight.getTime() - now.getTime();
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setRemaining(
        `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`,
      );
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  return remaining;
}

export default function LuckyToday() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const [lucky, setLucky] = useState<LuckyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const countdown = useCountdown();

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    setLoading(true);
    client
      .get("/features/lucky-today")
      .then((r) => setLucky(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const today = new Date()
    .toLocaleDateString("ko-KR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
    .replace(/\./g, ".")
    .trim();

  return (
    <div className="min-h-screen bg-[var(--color-bg)] p-4 sm:p-6">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-primary)] p-1"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <div>
            <h1 className="text-xl font-bold text-[var(--color-text-primary)]">🍀 오늘의 행운</h1>
            <p className="text-[var(--color-text-tertiary)] text-xs">{today}</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="lg" />
          </div>
        ) : lucky ? (
          <>
            {/* 영수증 */}
            <div className="bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-2xl p-6 font-mono">
              <div className="text-center text-[var(--color-accent)] font-bold text-lg mb-1">
                🏪 운명편의점
              </div>
              <div className="border-t border-dashed border-[var(--color-border-default)] my-3" />
              <div className="text-[var(--color-text-tertiary)] text-xs text-center mb-1">
                {lucky.day_gan}
                {lucky.day_ji}일의 행운 영수증
              </div>
              <div className="text-[var(--color-text-disabled)] text-xs text-center mb-3">
                {today}
              </div>
              <div className="border-t border-dashed border-[var(--color-border-default)] my-3" />

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-tertiary)]">🎨 행운 색상</span>
                  <span className="text-[var(--color-text-primary)] font-medium">
                    {lucky.lucky_color}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-tertiary)]">🔢 행운 숫자</span>
                  <span className="text-[var(--color-text-primary)] font-medium">
                    {lucky.lucky_number}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[var(--color-text-tertiary)]">🧭 행운 방향</span>
                  <span className="text-[var(--color-text-primary)] font-medium">
                    {lucky.lucky_direction}
                  </span>
                </div>
                <div className="flex justify-between items-start">
                  <span className="text-[var(--color-text-tertiary)]">🍽️ 행운 음식</span>
                  <div className="text-right">
                    <span className="text-[var(--color-text-primary)] font-medium">
                      {lucky.lucky_food}
                    </span>
                    <p className="text-[var(--color-text-disabled)] text-[10px] mt-0.5">천간 에너지 기반 행운 아이템</p>
                  </div>
                </div>
              </div>

              <div className="border-t border-dashed border-[var(--color-border-default)] my-3" />
              <div className="space-y-2.5">
                <p className="text-[var(--color-text-tertiary)] text-xs mb-2">📅 오늘의 흐름</p>
                <div className="flex gap-2 items-start">
                  <span className="text-green-400 text-xs font-bold mt-0.5 shrink-0">기회</span>
                  <p className="text-[var(--color-text-secondary)] text-xs leading-relaxed">{lucky.opportunity}</p>
                </div>
                <div className="flex gap-2 items-start">
                  <span className="text-[var(--color-accent)] text-xs font-bold mt-0.5 shrink-0">주의</span>
                  <p className="text-[var(--color-text-secondary)] text-xs leading-relaxed">{lucky.caution}</p>
                </div>
              </div>

              {lucky.lotto_numbers && lucky.lotto_numbers.length > 0 && (
                <>
                  <div className="border-t border-dashed border-[var(--color-border-default)] my-3" />
                  <div>
                    <p className="text-[var(--color-text-tertiary)] text-xs mb-1">
                      🎱 오늘의 행운 로또 번호
                    </p>
                    <div className="flex gap-1.5 justify-center my-2">
                      {lucky.lotto_numbers.map((n) => (
                        <LottoBall key={n} num={n} />
                      ))}
                    </div>
                    <p className="text-[var(--color-text-disabled)] text-[10px] text-center leading-tight">
                      재미로만 봐주세요 😊 당첨을 보장하지 않습니다
                    </p>
                  </div>
                </>
              )}

              <div className="border-t border-dashed border-[var(--color-border-default)] my-3" />
              <div className="text-center text-[var(--color-text-disabled)] text-xs">
                * 내일 또 오세요 🏪
              </div>
            </div>

            {/* 자정 갱신 카운트다운 */}
            <div className="mt-4 bg-[var(--color-surface)] border border-[var(--color-border-default)] rounded-xl px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-[var(--color-text-tertiary)] text-xs">다음 갱신까지</p>
                <p className="text-[var(--color-accent)] font-mono text-lg font-bold tracking-wider">
                  {countdown}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[var(--color-text-disabled)] text-xs">매일 자정 00:00</p>
                <p className="text-[var(--color-text-disabled)] text-xs">
                  새로운 행운으로 갱신됩니다
                </p>
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="flex gap-3 mt-4">
              <button
                onClick={() =>
                  navigate("/counseling", {
                    state: {
                      autoStart: true,
                      category: "general",
                      mode: "chat",
                    },
                  })
                }
                className="flex-1 py-3 bg-[var(--color-accent)] hover:opacity-90 text-[var(--color-primary)] rounded-xl text-sm font-bold transition-opacity"
              >
                💬 오늘 운세 더 상담하기
              </button>
            </div>
          </>
        ) : (
          <div className="text-center text-[var(--color-text-tertiary)] py-20">
            <p className="text-4xl mb-4">🍀</p>
            <p>행운 정보를 불러올 수 없습니다.</p>
          </div>
        )}
      </div>
    </div>
  );
}
